/**
 * @file Set of helper functions
 */

import {
  LabelBasicFragment,
  MessageStatus,
  UserBasicFragment,
  UserGroupBasicFragment,
} from '../../generated/graphql';
import { DraftDataFormatted } from '../../models/draft';
import { DraftAttachmentDataFormatted } from '../../models/draftAttachment';
import { searchLabels } from '../../utils/labels/filterLabels';
import searchGroups from '../../utils/members/filterGroups';
import { searchMembers } from '../../utils/members/filterMembers';

type DraftGroups = {
  assigneeSearch: string;
  assigneeGroupsCurrent: Set<string>;
};

type DraftLabels = {
  labelsSearch: string;
};

type DraftMembers = {
  assigneeSearch: string;
  assigneeMembersCurrent: Set<string>;
};

/**
 * Get assigned groups
 *
 * @param groupsAll         All groups that can be assigned to the message
 * @param assignedGroupsIds Ids of assigned groups
 * @returns                 All assigned groups
 */
export const getGroupsAssigned = (
  groupsAll: UserGroupBasicFragment[],
  assignedGroupsIds: Set<UserGroupBasicFragment['id']>,
): UserGroupBasicFragment[] => {
  return groupsAll.filter(member => assignedGroupsIds.has(member.id));
};

/**
 * Get which groups should be visible
 *
 * @param groupsAll All groups that can be assigned to the message
 * @param draft     Draft data
 * @returns         All the groups to show
 */
export const getGroupsVisible = (
  groupsAll: UserGroupBasicFragment[],
  draft: DraftGroups,
): UserGroupBasicFragment[] => {
  if (draft.assigneeSearch === '') {
    return groupsAll;
  }

  return searchGroups(draft.assigneeSearch, groupsAll);
};

/**
 * Get selected labels
 *
 * @param labelsAll         All labels that could be selected
 * @param selectedLabelsIds Ids of selected labels
 * @returns                 All selected labels
 */
export const getLabelsSelected = (
  labelsAll: LabelBasicFragment[],
  selectedLabelsIds: Set<LabelBasicFragment['id']>,
): LabelBasicFragment[] => {
  return labelsAll.filter(label => selectedLabelsIds.has(label.id));
};

/**
 * Get which labels should be visible
 *
 * @param labelsAll All labels that could be selected
 * @param draft     Draft data
 * @returns         All the labels to show
 */
export const getLabelsVisible = (
  labelsAll: LabelBasicFragment[],
  draft: DraftLabels,
): LabelBasicFragment[] => {
  if (draft.labelsSearch === '') {
    return labelsAll;
  }

  return searchLabels(draft.labelsSearch, labelsAll);
};

/**
 * Get assigned members
 *
 * @param membersAll         All members that can be assigned to the message
 * @param assignedMembersIds Ids of assigned members
 * @returns                  All assigned members
 */
export const getMembersAssigned = (
  membersAll: UserBasicFragment[],
  assignedMembersIds: Set<UserBasicFragment['id']>,
): UserBasicFragment[] => {
  return membersAll.filter(member => assignedMembersIds.has(member.id));
};

/**
 * Get which members should be visible
 *
 * @param membersAll All members that can be assigned to the message
 * @param draft      Draft data
 * @returns          All the members to show
 */
export const getMembersVisible = (
  membersAll: UserBasicFragment[],
  draft: DraftMembers,
): UserBasicFragment[] => {
  if (draft.assigneeSearch === '') {
    return membersAll;
  }

  return searchMembers(draft.assigneeSearch, membersAll);
};

/**
 * Get whether the default draft values have been changed
 *
 * @param draft            Draft textual data
 * @param draftAttachments Draft attachments data
 *
 * @returns                Whether the draft has been changed
 */
export const getIsDefaultDraftChanged = (
  draft?: DraftDataFormatted,
  draftAttachments?: DraftAttachmentDataFormatted,
) => {
  if (draft === undefined || draftAttachments === undefined) {
    return false;
  }

  const {
    assigneeGroupsSaved,
    assigneeMembersSaved,
    checklistSaved,
    dateDueSaved,
    labelsSaved,
    offsetSaved,
    remindersSaved,
    text,
    title,
    status,
  } = draft;

  return (
    draftAttachments.attachments.size > 0 ||
    title.length > 0 ||
    text.length > 0 ||
    assigneeGroupsSaved.size > 0 ||
    assigneeMembersSaved.size > 0 ||
    checklistSaved.length > 0 ||
    dateDueSaved !== null ||
    labelsSaved.size > 0 ||
    offsetSaved !== null ||
    remindersSaved.length > 0 ||
    (status !== null && status !== MessageStatus.OPEN)
  );
};

/**
 * Get whether the draft has been changed after being populated with default values
 *
 * @param draft            Draft textual data
 * @param draftAttachments Draft attachments data
 *
 * @returns                Whether the draft has been changed
 */
export const getIsDefaultDraftDataChanged = (
  draft?: DraftDataFormatted,
  draftAttachments?: DraftAttachmentDataFormatted,
) => {
  return getIsDefaultDraftChanged(draft, draftAttachments);
};

/**
 * Get whether the draft has been changed
 *
 * @param draft            Draft textual data
 * @param draftAttachments Draft attachments data
 *
 * @returns                Whether the draft has been changed
 */
export const getIsDraftChanged = (
  draft?: DraftDataFormatted,
  draftAttachments?: DraftAttachmentDataFormatted,
) => {
  if (draft === undefined || draftAttachments === undefined) {
    return false;
  }
  const isDefaultDraftChanged = getIsDefaultDraftChanged(
    draft,
    draftAttachments,
  );

  return isDefaultDraftChanged || draft.templateTopicSaved !== null;
};

/**
 * Get whether the initial draft has been changed
 *
 * @param isDefaultDraftChanged Whether the default draft has been changed
 * @param topicIdParam          The topic ID from the URL parameters
 * @param topicIdDraft          The topic ID from the draft data
 * @returns                     Whether the initial draft has been changed
 */
export const getIsInitialDraftChanged = (
  isDefaultDraftChanged: boolean,
  topicIdParam: string | null,
  topicIdDraft: string | undefined | null,
) => {
  return isDefaultDraftChanged || topicIdParam !== topicIdDraft;
};
